import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { auth } from '../services/api';

const AuthContext = createContext(null);

// How often to probe the server to confirm our session is still alive.
// When this probe 401s we KNOW the session is dead (not just a transient
// error on some other endpoint). Logs a full diagnostic so we can see WHY
// — was the cookie missing, was the bearer token missing, did the JWT
// fail to verify, etc. Replace the previous "kicked out every ~5 min" pattern
// (single-endpoint 401s were triggering the api.js interceptor's blanket
// logout — see api.js comments for the full story).
const SESSION_PROBE_MS = 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track the last probe status for the diagnostic banner (so Matt can
  // see "session was probed 47 seconds ago, still good" if he wants to).
  const [lastProbeAt, setLastProbeAt] = useState(null);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Periodic session probe — only runs while we have a user AND the tab is
  // visible. Hidden tabs (especially aggressive tab-eviction browsers like
  // Arc, which put tabs to sleep aggressively) can cause spurious probe
  // failures when the tab wakes up and the server hasn't kept the connection
  // warm. Pausing the probe while hidden prevents 401-counter accumulation.
  useEffect(() => {
    if (!user) return;
    let intervalId = null;

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => probeSession(), SESSION_PROBE_MS);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
        console.debug('[auth-probe] tab hidden, pausing probe');
      } else {
        start();
        console.debug('[auth-probe] tab visible, resuming probe');
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    // Run one probe immediately on focus — if the tab was put to sleep
    // and just woke up, we want to know right away if the session died
    // (rather than waiting up to 60s for the next interval).
    const onFocus = () => {
      if (!document.hidden) {
        console.debug('[auth-probe] window focused, probing now');
        probeSession();
      }
    };
    window.addEventListener('focus', onFocus);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const probeSession = async () => {
    try {
      const t0 = Date.now();
      const res = await auth.me();
      const ms = Date.now() - t0;
      console.debug(`[auth-probe] OK in ${ms}ms user=${res.data?.username} id=${res.data?.id}`);
      setLastProbeAt(new Date().toISOString());
    } catch (err) {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const hasCookie = document.cookie.includes('token=');
      const lsToken = localStorage.getItem('token');
      console.warn(
        `[auth-probe] FAIL status=${status} url=${url} ` +
        `cookie=${hasCookie ? 'yes' : 'no'} ` +
        `localStorage_token=${lsToken ? 'yes(' + lsToken.length + ')' : 'no'} ` +
        `response_data=${JSON.stringify(err?.response?.data)} ` +
        `last_user_id=${lastUserIdRef.current}`
      );
      // DO NOT auto-logout here. The api.js interceptor will handle a
      // /auth/me 401 by logging out. We just log so we can see the
      // context in the browser console / server logs.
    }
  };

  const checkAuth = async () => {
    try {
      // The server sets an httpOnly `token` cookie on login. The cookie is
      // the source of truth for "is this browser logged in" — localStorage
      // is just a redundant convenience cache that api.js uses to build the
      // Authorization header. If localStorage is empty (browser cleared it,
      // another tab ran the api.js interceptor, user opened a second tab in
      // a private window that shares cookies but not localStorage, etc.)
      // we should STILL be logged in via the cookie and /auth/me should
      // confirm it.
      const cookieToken = document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1];
      const lsToken = localStorage.getItem('token');
      const token = lsToken || (cookieToken || null);

      // If we found the token in the cookie but not localStorage, repopulate
      // localStorage so the Authorization header keeps working in this tab.
      if (cookieToken && !lsToken) {
        try { localStorage.setItem('token', cookieToken); } catch (_) { /* private mode */ }
      }

      if (token) {
        const response = await auth.me();
        setUser(response.data);
        lastUserIdRef.current = response.data?.id;
        setLastProbeAt(new Date().toISOString());

        // Keep localStorage in sync with the most recent valid token. If the
        // server ever rotates the JWT (e.g. after a /auth/change-password)
        // we'd otherwise keep using a stale one until the next reload.
        const fresh = response.headers?.['x-refresh-token'] || response.data?.token;
        if (fresh && fresh !== token) {
          try { localStorage.setItem('token', fresh); } catch (_) {}
        }
      }
    } catch (error) {
      // Only nuke the token if the server explicitly says our session is
      // dead (401). Network errors, rate-limit 429, transient 5xx, CORS
      // preflight failures, etc. should NOT log the user out — keep the
      // token, log the diagnostic, and let the next probe figure it out.
      // The api.js interceptor handles the /auth/me 401 specifically with
      // a 3-strikes counter before redirecting.
      const status = error?.response?.status;
      const url = error?.config?.url;
      console.warn(
        `[auth] checkAuth FAIL status=${status} url=${url} ` +
        `— ${status === 401 ? 'session is dead, removing token' : 'keeping token, will retry'} ` +
        `(${error?.message?.slice(0, 80) || 'no message'})`
      );
      if (status === 401) {
        // Clear BOTH localStorage and the cookie on confirmed dead session.
        // api.js can't clear httpOnly cookies, so we ask the server via the
        // /auth/logout endpoint as a best-effort (fire-and-forget).
        try { localStorage.removeItem('token'); } catch (_) {}
        fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
          .catch(() => { /* non-fatal */ });
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await auth.login(username, password);
    localStorage.setItem('token', response.data.token || response.token);
    setUser(response.data.user || response.user);
    lastUserIdRef.current = response.data?.user?.id || response.user?.id;
    setLastProbeAt(new Date().toISOString());
    return response.data;
  };

  const logout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      lastUserIdRef.current = null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, checkAuth, lastProbeAt, probeSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
